/**
 * DaLuca / Habitat predictive search — products only.
 * Keeps the native header search UI but makes exact watch references useful.
 */
class PredictiveSearch {
  constructor() {
    this.container = document.querySelector('.thb-quick-search');
    this.button = document.getElementById('quick-search');
    if (!this.container || !this.button) return;
    this.close_button = this.container.querySelector('.thb-search-close');
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.container.querySelector('.thb-quick-search--results');
    this.cache = new Map();
    this.controller = null;
    this.watchDbPromise = null;
    this.requestId = 0;
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.container.querySelector('form.searchform');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 260).bind(this));

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      const header = document.querySelector('.header-section');
      const menu = document.querySelector('.dl-mobile-toggle-wrapper');
      const menuOpen = menu && (menu.hasAttribute('open') || menu.classList.contains('active'));
      if (menuOpen) {
        menu.removeAttribute('open');
        menu.classList.remove('active');
        menu.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden', 'dl-mobile-nav-open');
        document.documentElement.classList.remove('dl-mobile-nav-open');
      }
      const searchOpen = menuOpen || !header.classList.contains('search-open');
      if (searchOpen) this.open();
      else this.close();
    });

    if (this.close_button) {
      this.close_button.addEventListener('click', (event) => {
        this.close();
        event.preventDefault();
      });
    }
    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove('active');
      this.predictiveSearchResults.innerHTML = '';
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  compact(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  materialTerm(raw) {
    const q = String(raw || '').toLowerCase();
    const map = [
      ['shell cordovan', 'shell cordovan'],
      ['shell', 'shell cordovan'],
      ['suede', 'suede'],
      ['rubber', 'rubber'],
      ['nylon', 'nylon'],
      ['perlon', 'perlon'],
      ['vintage', 'vintage'],
      ['alligator', 'alligator'],
      ['lizard', 'lizard'],
      ['exotic', 'exotic'],
      ['leather', 'leather']
    ];
    const match = map.find(([needle]) => q.includes(needle));
    return match ? match[1] : '';
  }

  looksLikeReference(raw) {
    const q = this.compact(raw);
    return /\d{4,}/.test(q) || /^pam\d{2,}$/i.test(q);
  }

  loadWatchDb() {
    if (!this.watchDbPromise) {
      this.watchDbPromise = fetch('https://cdn.shopify.com/s/files/1/0857/0362/2962/files/daluca-watch-db-production-v20-CURATED.json?v=1788907294', { credentials: 'omit' })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => Array.isArray(data) ? data : (data && (data.records || data.watches || data.data)) || [])
        .catch(() => []);
    }
    return this.watchDbPromise;
  }

  async deriveProductQuery(raw) {
    if (!this.looksLikeReference(raw)) return raw;
    const records = await this.loadWatchDb();
    if (!records.length) return raw;
    const qc = this.compact(raw);
    let best = null;
    let bestScore = -1;
    for (const record of records) {
      if (!String(record.verification_status || '').toUpperCase().startsWith('VERIFIED')) continue;
      const refs = [record.reference, record.canonical_reference, record.display_reference].concat(record.aliases || []).filter(Boolean);
      for (const ref of refs) {
        const rc = this.compact(ref);
        if (!rc || rc.length < 4) continue;
        let score = -1;
        if (qc === rc) score = 100;
        else if (qc.endsWith(rc) || qc.includes(rc)) score = 80;
        if (score > bestScore) { best = record; bestScore = score; }
      }
    }
    if (!best || bestScore < 80) return raw;
    const width = Number(best.lug_width_mm || 0);
    if (!width) return raw;
    const material = this.materialTerm(raw);
    const ready = [18,20,22,24,26].includes(width);
    return [ready ? `${width}mm` : 'custom', material].filter(Boolean).join(' ');
  }

  async getSearchResults(searchTerm) {
    const requestId = ++this.requestId;
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    const cacheKey = searchTerm.toLowerCase();
    if (this.cache.has(cacheKey)) {
      this.renderSearchResults(this.cache.get(cacheKey));
      return;
    }

    this.predictiveSearchResults.classList.add('loading');
    const productQuery = await this.deriveProductQuery(searchTerm);
    if (requestId !== this.requestId) return;

    const params = new URLSearchParams({
      q: productQuery,
      'resources[type]': 'product',
      'resources[limit]': '12',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku,tag',
      section_id: 'predictive-search'
    });

    fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: this.controller.signal })
      .then((response) => {
        this.predictiveSearchResults.classList.remove('loading');
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        const markup = parsed ? parsed.innerHTML : '';
        if (this.cache.size >= 50) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(cacheKey, markup);
        this.renderSearchResults(markup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.classList.remove('active');
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.classList.add('active');
  }

  open() {
    document.querySelector('.header-section').classList.add('search-open');
    this.input.focus({ preventScroll: true });
    if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('search:open');
  }

  close() {
    const header = document.querySelector('.header-section');
    if (!header.classList.contains('search-open')) return;
    const returnFocus = this.container.contains(document.activeElement);
    header.classList.remove('search-open');
    if (returnFocus) this.button.focus({ preventScroll: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PredictiveSearch(), { once: true });
} else {
  new PredictiveSearch();
}
